import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header, { BreadcrumbItem } from '../components/Header';
import { usePageTracking } from '../hooks/usePageTracking';
import { meetingService, Meeting } from '../services/meetingService';
import { useAuth } from '../context/AuthContext';
import { useDialog } from '../context/DialogContext';
import styles from '../styles/meetingSettingsPage.module.css';

const JOIN_RULE_OPTIONS = [
  { value: 'anyone', label: 'Anyone (signed in)' },
  { value: 'meeting_key', label: 'Pass phrase required (default)' },
  { value: 'approval_required', label: 'Approval required' },
  { value: 'invite_only', label: 'Invite only' },
];

const FORMAT_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'in_person', label: 'In-Person' },
  { value: 'telemeeting', label: 'Telemeeting' },
];

interface MeetingSettingsPageProps {
  user?: any;
  onLogout: () => void;
  onLogin: () => void;
}

const MeetingSettingsPage: React.FC<MeetingSettingsPageProps> = ({
  user,
  onLogout,
  onLogin,
}) => {
  const { code } = useParams<{ code: string }>();
  const { user: authUser } = useAuth();
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [format, setFormat] = useState('online');
  const [location, setLocation] = useState('');
  const [telemeetingDialIn, setTelemeetingDialIn] = useState('');
  const [telemeetingPasscode, setTelemeetingPasscode] = useState('');
  const [joinRule, setJoinRule] = useState('meeting_key');
  const [joinKeyInput, setJoinKeyInput] = useState('');
  const [customPassPhraseInput, setCustomPassPhraseInput] = useState('');
  const [members, setMembers] = useState<{ user_id: string; display_name: string; is_admin: boolean }[]>([]);

  const effectiveUser = user || authUser;
  const { showConfirm } = useDialog();

  usePageTracking(`Meeting Settings: ${code || 'unknown'}`);

  useEffect(() => {
    if (!code || !effectiveUser) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [m, status] = await Promise.all([
          meetingService.getMeetingByCodeForAdmin(code),
          meetingService.getMemberStatus(code),
        ]);
        setMeeting(m);
        if (!status.is_member) {
          setError('You must be a member to view this page.');
          return;
        }
        if (!status.is_admin) {
          setError('Only meeting admins can change settings.');
          return;
        }
        setName(m.name ?? '');
        setDescription(m.description ?? '');
        setFormat((m.format as string) ?? 'online');
        setLocation(m.location ?? '');
        setTelemeetingDialIn(m.telemeeting_dial_in ?? '');
        setTelemeetingPasscode(m.telemeeting_passcode ?? '');
        setJoinRule((m.join_rule as string) ?? 'meeting_key');
        setJoinKeyInput((m.join_key as string) ?? '');
        const memberList = await meetingService.listMembers(code);
        setMembers(memberList);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Meeting not found');
        setMeeting(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [code, effectiveUser]);

  const backPath = `/meetings/${code}`;

  const handleRemoveMember = (member: { user_id: string; display_name: string }) => {
    showConfirm({
      title: 'Remove from meeting',
      message: `Remove ${member.display_name} from this meeting? They will need to add themselves again (or use the join key) to rejoin.`,
      confirmText: 'Remove from meeting',
      cancelText: 'Cancel',
      variant: 'danger',
      onConfirm: async () => {
        if (!code) return;
        await meetingService.removeMember(code, member.user_id);
        setMembers((prev) => prev.filter((m) => m.user_id !== member.user_id));
      },
    });
  };

  const handleCopyJoinLink = () => {
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    const url = `${base}/meeting/join/${encodeURIComponent((meeting?.meeting_code || code || '').toUpperCase())}/${encodeURIComponent((meeting?.join_key || joinKeyInput || '').toUpperCase())}`;
    navigator.clipboard.writeText(url).then(() => setSaveMessage('Join link copied to clipboard.'));
  };

  const handleRegeneratePassPhrase = async () => {
    if (!code || saving) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      await meetingService.updateMeeting(code, { regenerate_pass_phrase: true });
      const m = await meetingService.getMeetingByCodeForAdmin(code);
      setMeeting(m);
      setJoinKeyInput((m.join_key as string) ?? '');
      setCustomPassPhraseInput('');
      setSaveMessage('Pass phrase regenerated. Share the new join link.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code || saving) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      await meetingService.updateMeeting(code, {
        name: name.trim() || (meeting?.name ?? ''),
        description: description.trim(),
        format,
        location: location.trim(),
        telemeeting_dial_in: format === 'telemeeting' ? telemeetingDialIn.trim() : undefined,
        telemeeting_passcode: format === 'telemeeting' ? telemeetingPasscode.trim() : undefined,
        join_rule: joinRule,
        ...(customPassPhraseInput.trim() ? { join_key: customPassPhraseInput.trim() } : {}),
      });
      setSaveMessage('Settings saved.');
      if (customPassPhraseInput.trim()) {
        const m = await meetingService.getMeetingByCodeForAdmin(code);
        setMeeting(m);
        setJoinKeyInput((m.join_key as string) ?? '');
        setCustomPassPhraseInput('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const programId = (meeting?.program_id || 'saa').toLowerCase();
  const programLabel = (meeting?.program_id || 'SAA').toUpperCase();
  const formatLabel = (fmt: string | undefined) =>
    ({ online: 'Online', hybrid: 'Hybrid', in_person: 'In-Person', telemeeting: 'Telemeeting' }[fmt || 'online'] || 'Online');
  const meetingBreadcrumbLabel = meeting
    ? `${(meeting.name || '').trim() || formatLabel(meeting.format)} (${meeting.meeting_code})`
    : (code || 'Meeting');
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: programLabel, path: `/${programId}`, active: false },
    { label: 'Meetings', path: `/${programId}?tab=meetings`, active: false },
    { label: meetingBreadcrumbLabel, path: backPath, active: false },
    { label: 'Settings', path: '', active: true },
  ];

  if (!effectiveUser) {
    return (
      <div className={styles.page}>
        <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
        <main className={styles.main}>
          <p className={styles.error}>Sign in to view meeting settings.</p>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
        <main className={styles.main}>
          <p className={styles.loading}>Loading…</p>
        </main>
      </div>
    );
  }

  if (error || !meeting) {
    return (
      <div className={styles.page}>
        <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
        <main className={styles.main}>
          <p className={styles.error}>{error || 'Meeting not found'}</p>
          <a href={backPath} className={styles.backLink}>← Back to meeting</a>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
      <main className={styles.main}>
        <h1 className={styles.title}>Meeting settings</h1>
        <form onSubmit={handleSubmit} className={styles.form}>
          <label className={styles.label}>Meeting name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={styles.input}
            required
          />
          <label className={styles.label}>Description (optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={styles.textarea}
            rows={3}
          />
          <label className={styles.label}>Format</label>
          <select value={format} onChange={(e) => setFormat(e.target.value)} className={styles.select}>
            {FORMAT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {(format === 'hybrid' || format === 'in_person') && (
            <>
              <label className={styles.label}>Location</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className={styles.input}
              />
            </>
          )}
          {format === 'telemeeting' && (
            <>
              <label className={styles.label}>Dial-in number</label>
              <input
                type="text"
                value={telemeetingDialIn}
                onChange={(e) => setTelemeetingDialIn(e.target.value)}
                className={styles.input}
              />
              <label className={styles.label}>Passcode (optional)</label>
              <input
                type="text"
                value={telemeetingPasscode}
                onChange={(e) => setTelemeetingPasscode(e.target.value)}
                className={styles.input}
              />
            </>
          )}
          <label className={styles.label}>Who can join?</label>
          <select
            value={joinRule}
            onChange={(e) => setJoinRule(e.target.value)}
            className={styles.select}
          >
            {JOIN_RULE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {joinRule === 'meeting_key' && (
            <div className={styles.passPhraseBlock}>
              <p className={styles.passPhraseIntro}>
                Share the join link with people who should join; the link includes the pass phrase so they don&apos;t need to type it.
              </p>
              <div className={styles.joinLinkRow}>
                <code className={styles.joinUrl}>
                  {typeof window !== 'undefined'
                    ? `${window.location.origin}/meeting/join/${(meeting?.meeting_code || code || '').toUpperCase()}/${(meeting?.join_key || joinKeyInput || '').toUpperCase()}`
                    : ''}
                </code>
                <button type="button" onClick={handleCopyJoinLink} className={styles.copyLinkBtn}>
                  Copy join link
                </button>
              </div>
              <p className={styles.passPhraseHint}>Pass phrase: <strong>{(meeting?.join_key || joinKeyInput || '').toUpperCase() || '—'}</strong></p>
              <div className={styles.changePassPhraseRow}>
                <input
                  type="text"
                  value={customPassPhraseInput}
                  onChange={(e) => setCustomPassPhraseInput(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
                  placeholder="Custom pass phrase (optional)"
                  className={styles.input}
                  autoComplete="off"
                />
                <button type="button" onClick={handleRegeneratePassPhrase} disabled={saving} className={styles.regenerateBtn}>
                  Regenerate
                </button>
              </div>
            </div>
          )}
          {saveMessage && <p className={styles.saveMessage}>{saveMessage}</p>}
          <div className={styles.formActions}>
            <button type="submit" className={styles.submitBtn} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <a href={backPath} className={styles.backLink}>← Back to meeting</a>
          </div>
        </form>

        <section className={styles.membersSection}>
          <h2 className={styles.membersTitle}>Members & permissions</h2>
          <p className={styles.membersIntro}>Only meeting admins can see this list. You can remove a member; they can rejoin if the meeting allows it.</p>
          {members.length === 0 ? (
            <p className={styles.membersEmpty}>No members yet.</p>
          ) : (
            <ul className={styles.membersList}>
              {members.map((m) => (
                <li key={m.user_id} className={styles.memberRow}>
                  <span className={styles.memberName}>{m.display_name}</span>
                  {m.is_admin && <span className={styles.adminBadge}>Admin</span>}
                  {m.user_id !== effectiveUser?.email && (
                    <button
                      type="button"
                      className={styles.removeMemberBtn}
                      onClick={() => handleRemoveMember(m)}
                    >
                      Remove from meeting
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
};

export default MeetingSettingsPage;
